import React, { useCallback, useMemo, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Divider, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { AutocompleteOption } from "@components/Autocomplete/Autocomplete.types";
import Button from "@components/Button/Button";
import BaseTable from "@components/DataTable/BaseTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import SideDrawer from "@components/SideDrawer/SideDrawer";
import { FORM_FIELDS } from "@features/vessel-call/create/constants";
import type { VesselCallOutletContext } from "@features/vessel-call/create/CreateVesselCallContainer";
import {
  useVesselCallFormStore,
  type PortCall,
} from "@features/vessel-call/create/store/useVesselCallForm.store.ts";
import { buildPortOptions } from "@features/vessel-call/create/utils/portOptions.ts";
import { sortPortCallsByClosestDates } from "@features/vessel-call/utils/sortPortCallsByClosestDates.ts";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Country, Port } from "@services/vessel/types";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { type PortCallsSchema } from "./PortCalls.schema";

export const LAST_TEN_PORTS = Object.freeze({
  port: "port",
  country: "country",
  securityLevelOfPortCall: "security-level-of-port-call",
  arrivalDate: "arrival-date",
  departureDate: "departure-date",
  comments: "comments",
  action: "action",
});

const LANG_BASE = "vessel-call.last-ten-ports";
const LANG_BASE_HEADER = `${LANG_BASE}.grid-headers`;
const LANG_BASE_SIDEBAR = `${LANG_BASE}.sidebar`;
const TEST_ID_BASE = "port-calls";

const PortCalls: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { portCalls, addPortCall, addPortCallDraft, setAddPortCallDraft, clearAddPortCallDraft } =
    useVesselCallFormStore();
  const { formData, isFinalSubmission } = useOutletContext<VesselCallOutletContext>();
  const currentPortCalls = formData.watch(FORM_FIELDS.PORT_CALLS) as PortCall[];

  const { options: ports } = useFormattedReference<Port>(REFERENCE_DATA_TYPES.PORTS, "bff-vessel");
  const { options: countries } = useFormattedReference<Country>(
    REFERENCE_DATA_TYPES.COUNTRIES,
    "bff-vessel",
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingPortCall, setEditingPortCall] = useState<PortCall | null>(null);

  const syncAndValidatePortCalls = useCallback(
    (nextPortCalls: PortCall[]) => {
      formData.setValue(FORM_FIELDS.PORT_CALLS, sortPortCallsByClosestDates(nextPortCalls), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      void formData.trigger([FORM_FIELDS.PORT_CALLS, FORM_FIELDS.LAST_PORT]);
    },
    [formData],
  );

  const openAddDrawer = () => {
    setEditingPortCall(null);
    setIsDrawerOpen(true);
  };

  const handlePersistPortCallDraft = useCallback(
    (draft: Partial<PortCallsSchema> | null) => {
      if (!editingPortCall) {
        setAddPortCallDraft(draft);
      }
    },
    [editingPortCall, setAddPortCallDraft],
  );

  const openEditDrawer = (row: PortCallsSchema & { id?: string }) => {
    setEditingPortCall({
      id: row.id ?? "",
      port: row.port,
      country: row.country,
      securityLevelOfPortCall: row.securityLevelOfPortCall,
      arrivalDate: typeof row.arrivalDate === "string" ? row.arrivalDate : "",
      departureDate: typeof row.departureDate === "string" ? row.departureDate : "",
      comments: row.comments ?? "",
    });
    setIsDrawerOpen(true);
  };
  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  function handleAddOrEditPortCalls(data?: PortCallsSchema) {
    if (!data) return;

    const toISO = (d?: Date | string) => {
      if (!d) return "";
      const dateObj = typeof d === "string" ? new Date(d) : d;
      return dateObj.toISOString();
    };

    if (editingPortCall) {
      const updated: PortCall = {
        ...editingPortCall,
        id: editingPortCall.id,
        port: data?.port,
        country: data?.country,
        arrivalDate: toISO(data?.arrivalDate),
        departureDate: toISO(data?.departureDate),
        securityLevelOfPortCall: data?.securityLevelOfPortCall,
        comments: data?.comments ?? "",
      };

      useVesselCallFormStore.setState((s) => ({
        ...s,
        portCalls: sortPortCallsByClosestDates(
          s.portCalls.map((m) => (m.id === updated.id ? updated : m)),
        ),
      }));

      syncAndValidatePortCalls(currentPortCalls.map((m) => (m.id === updated.id ? updated : m)));

      setEditingPortCall(null);
      closeDrawer();
      return;
    }

    const newPortCall: PortCall = {
      id: crypto.randomUUID(),
      port: data.port,
      country: data.country,
      arrivalDate: toISO(data.arrivalDate),
      departureDate: toISO(data.departureDate),
      securityLevelOfPortCall: data.securityLevelOfPortCall,
      comments: data.comments ?? "",
    };

    addPortCall(newPortCall);

    syncAndValidatePortCalls([...currentPortCalls, newPortCall]);

    clearAddPortCallDraft();
    closeDrawer();
  }

  const onRowDelete = (row: PortCall) => {
    useVesselCallFormStore.setState((s) => ({
      ...s,
      portCalls: sortPortCallsByClosestDates(s.portCalls.filter((m) => m.id !== row.id)),
    }));
    syncAndValidatePortCalls(currentPortCalls.filter((m) => m.id !== row.id));
  };

  const onRowEdit = (row: PortCall) => {
    const original = portCalls?.find((p) => p.id === row.id);
    openEditDrawer(original ?? row);
  };

  const headerConfig: Array<{
    field: keyof PortCall | "action";
    translationKey: string;
    isDate?: boolean;
  }> = [
    { field: "port", translationKey: LAST_TEN_PORTS.port },
    { field: "country", translationKey: LAST_TEN_PORTS.country },
    { field: "arrivalDate", translationKey: LAST_TEN_PORTS.arrivalDate, isDate: true },
    { field: "departureDate", translationKey: LAST_TEN_PORTS.departureDate, isDate: true },
    { field: "securityLevelOfPortCall", translationKey: LAST_TEN_PORTS.securityLevelOfPortCall },
    { field: "comments", translationKey: LAST_TEN_PORTS.comments },
    { field: "action", translationKey: LAST_TEN_PORTS.action },
  ];

  const headers: Header[] = useMemo(
    () =>
      headerConfig.map(({ field, translationKey, isDate }) => ({
        field,
        columnName: t(`${LANG_BASE_HEADER}.${translationKey}`),
        ...(isDate ? { type: "date", dateFormat: "dd/MM/yyyy" } : {}),
      })),
    [t],
  );

  const isEditing = !!editingPortCall;

  const fieldError = (formData.formState.errors as Record<string, { message?: string }>)[
    "portCalls"
  ]?.message;
  const hasTableError = Boolean(fieldError);

  const getPortLabelByCode = (code?: string) => {
    if (!code) return "";

    const port = ports?.find((p) => p.portCode === code);
    return port ? `${port.portName} (${port.portCode})` : null;
  };

  const countryNameByCode = useMemo(() => {
    const map = new Map<string, string>();

    countries.forEach((country) => {
      if (country?.countryCode && country?.countryName) {
        map.set(country.countryCode, `${country.countryName} (${country.countryCode})`);
      }
    });

    return map;
  }, [countries]);

  const tableRows: PortCall[] = useMemo(() => {
    return sortPortCallsByClosestDates(portCalls ?? []).map((item, idx) => {
      const portLabel = getPortLabelByCode(item.port) ?? item.port;
      const countryLabel = countryNameByCode.get(item.country) ?? item.country;
      return {
        id: (item as any).id ?? `portcall-${idx}`,
        port: portLabel ?? "",
        country: countryLabel ?? "",
        arrivalDate: typeof item.arrivalDate === "string" ? item.arrivalDate : "",
        departureDate: typeof item.departureDate === "string" ? item.departureDate : "",
        securityLevelOfPortCall: t(
          `vessel-call.create.security-levels.level-${item.securityLevelOfPortCall}`,
        ),
        comments: (item as any).comments ?? "",
      };
    });
  }, [portCalls, ports, countryNameByCode, t]);

  const maximumPortCallsReached = portCalls && portCalls.length >= 10;

  const countriesOptions = useMemo<AutocompleteOption[]>(
    () =>
      countries
        .filter(
          (country): country is Country & { countryCode: string; countryName: string } =>
            !!(country?.countryCode && country?.countryName),
        )
        .map(({ countryCode, countryName }) => ({
          value: countryCode,
          label: `${countryName} (${countryCode})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [countries],
  );
  const portCountryCodeByPortCode = useMemo(() => {
    const mapPorts = new Map<string, string>();
    ports?.forEach((port) => {
      if (port?.portCode && port?.countryCode) {
        mapPorts.set(String(port.portCode), port.countryCode);
      }
    });

    return mapPorts;
  }, [ports]);
  const getPortsOptions = useCallback(
    (selectedCountryCode?: string): AutocompleteOption[] => {
      const filteredPorts =
        ports
          ?.filter(
            (port): port is Port & { portCode: string; portName: string } =>
              !!(port?.portCode && port?.portName),
          )
          .filter((port) => !selectedCountryCode || port.countryCode === selectedCountryCode) ?? [];

      return buildPortOptions(filteredPorts);
    },
    [ports],
  );
  const handlePortCallPortChange = useCallback(
    (opt: AutocompleteOption | null, portCallForm: UseFormReturn<PortCallsSchema>) => {
      portCallForm.setValue("port", (opt?.value as string) ?? "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      if (opt?.value == null) {
        portCallForm.setValue("country", "", {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        return;
      }

      const countryCode = portCountryCodeByPortCode.get(String(opt.value));
      if (countryCode) {
        portCallForm.setValue("country", countryCode, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    },
    [portCountryCodeByPortCode],
  );

  const handlePortCallCountryChange = useCallback(
    (opt: AutocompleteOption | null, portCallForm: UseFormReturn<PortCallsSchema>) => {
      portCallForm.setValue("country", (opt?.value as string) ?? "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      portCallForm.setValue("port", "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [portCountryCodeByPortCode],
  );

  return (
    <div className="flex flex-col items-end gap-4" data-testid={TEST_ID_BASE}>
      <div className="flex flex-col w-full gap-4">
        <Typography variant="subtitle1">{t(`${LANG_BASE}.title`)}</Typography>
        <BaseTable
          rows={tableRows}
          headers={headers}
          onRowEdit={onRowEdit}
          onRowDelete={onRowDelete}
          customSx={
            hasTableError
              ? {
                  border: 1,
                  borderColor: theme.palette.error.main,
                }
              : undefined
          }
        />
      </div>

      <Divider className="w-full" />

      <div className="flex w-full justify-between items-center">
        <Typography color="error" variant="caption" sx={{ fontWeight: 500, fontSize: "12px" }}>
          {fieldError && (fieldError as string)}
        </Typography>

        {!maximumPortCallsReached && (
          <Button
            type="button"
            variant="outlined"
            color="primary"
            testId="page-header-register-vessel-button"
            label={
              <span className="text-sm font-medium flex items-center gap-2">
                <AddCircleOutlineIcon fontSize="small" />
                {t(`${LANG_BASE}.button`)}
              </span>
            }
            onClick={openAddDrawer}
            sx={{ textTransform: "none", height: "40px", minHeight: "40px" }}
          />
        )}
      </div>

      {isDrawerOpen && (
        <SideDrawer
          type={isEditing ? "editPortCall" : "addPortCall"}
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          title={t(`${LANG_BASE_SIDEBAR}.${isEditing ? "title-edit" : "title-add"}`)}
          description={t(`${LANG_BASE_SIDEBAR}.description`)}
          submitButtonLabel={t(`${LANG_BASE_SIDEBAR}.buttons.${isEditing ? "edit" : "add"}`)}
          onSubmit={handleAddOrEditPortCalls}
          portCallExistingEntries={portCalls ?? []}
          portCallCurrentId={editingPortCall?.id}
          portCallEnforceUniqueDates={isFinalSubmission}
          portCallPortsOptions={getPortsOptions}
          portCallCountriesOptions={countriesOptions}
          onPortCallPortChange={handlePortCallPortChange}
          onPortCallCountryChange={handlePortCallCountryChange}
          portCallInitialValues={
            isEditing
              ? {
                  port: editingPortCall!.port,
                  country: editingPortCall!.country,
                  securityLevelOfPortCall: editingPortCall!.securityLevelOfPortCall,
                  arrivalDate: editingPortCall!.arrivalDate
                    ? new Date(editingPortCall!.arrivalDate)
                    : undefined,
                  departureDate: editingPortCall!.departureDate
                    ? new Date(editingPortCall!.departureDate)
                    : undefined,
                  comments: editingPortCall!.comments ?? "",
                }
              : (addPortCallDraft ?? undefined)
          }
          onPersistDraft={handlePersistPortCallDraft}
        />
      )}
    </div>
  );
};

export default PortCalls;
